import { useMemo, useState } from "react"
import { DateTime } from "@/lib/dates"
import { useTrans } from "@/i18n"
import { ChevronDownIcon } from "@/lib/icons"

// Animations
import {
	Popover,
	PopoverGroup,
	PopoverButton,
	PopoverPanel,
	Transition,
} from "@/lib/ui"
import { getTransitionPopoverProps } from "@/lib/animations"

// UI
import { cn } from "@/lib/utils"
import { DatePicker } from "../../../components/form-controls/DatePicker"

// Graphs
import { useProductionGraphContext } from "../../../components/graphs/types/bar/ProductionGraphContext"
import { GraphActions } from "../../../components/graphs/header/GraphActions"

// Feature flags
import { useFeatureFlags } from "@/context/user"

/**
 * ProjectGraphProductionActions
 * @returns
 */
export function ProjectGraphProductionActions() {
	const { graphInterval, setGraphInterval } = useProductionGraphContext()
	const AvailableDatePeriods = ["day", "month", "year"]
	const { getFeatureFlagValue } = useFeatureFlags()

	return (
		<>
			{/* date picker */}
			{getFeatureFlagValue("PROJECT_DISPLAY_DATE_SELECTOR") && (
				<DateRangePicker />
			)}

			<GraphActions
				graphInterval={graphInterval}
				setGraphInterval={setGraphInterval}
				availableIntervals={AvailableDatePeriods}
			/>
		</>
	)
}

function DateRangePicker() {
	// Translate
	const t = useTrans("project")

	// Date
	const minimumDate = DateTime.local().minus({ weeks: 1 }).endOf("day")
	const maximumDate = DateTime.local().endOf("day")
	const currentDateTime = DateTime.local()
	const [date, setDate] = useState<Date>(maximumDate.toJSDate())

	// months to show in date picker
	const months = useMemo(() => {
		if (maximumDate.month !== minimumDate.month) {
			return [minimumDate.month, maximumDate.month]
		}
		return [maximumDate.month]
	}, [maximumDate.month, minimumDate.month])

	return (
		<div className="mr-6 items-center lg:flex">
			<h2 className="font-static text-dark mr-6 truncate text-base font-extrabold uppercase">
				{t("project:data.energy_producted.button.custom")}
			</h2>

			{/** Date Start */}
			<PopoverGroup className="flex items-center divide-x divide-gray-200">
				<Popover className="relative z-10 inline-block text-right">
					<PopoverButton
						className={cn(
							"group relative inline-flex items-center justify-center rounded-none",
							"border border-l-0 border-gray-300 bg-white px-3 py-2 text-sm font-medium",
							"text-gray-700 hover:bg-gray-50 hover:text-gray-900",
							"focus:ring-primary-500 focus:z-20 focus:ring-1 focus:outline-hidden",
						)}
					>
						<span className="mr-2">
							{DateTime.fromJSDate(date).toFormat(
								t(
									"project:data.production_today.date_picker.title.date_format",
								),
							)}
						</span>

						<ChevronDownIcon
							className="size-5 shrink-0 text-gray-400 group-hover:text-gray-500"
							aria-hidden="true"
						/>
					</PopoverButton>
					<Transition {...getTransitionPopoverProps()}>
						<PopoverPanel className="absolute left-0 mt-2 origin-top-right lg:right-0 lg:left-auto">
							<div
								className="min-w-[300px]"
								data-testid="daterangepicker-from"
							>
								<DatePicker
									initialMonth={currentDateTime.month}
									initialYear={currentDateTime.year}
									selectedDate={currentDateTime}
									showChangeOfYear={false}
									showNavigation={false}
									months={months}
									onSelectDate={(date) => {
										const newDate =
											DateTime.fromObject(date).endOf(
												"day",
											)
										setDate(newDate.toJSDate())
									}}
									isDateDisabled={(date) => {
										const dateTime = DateTime.fromObject({
											...date,
											day: date.day + 1,
										})

										// is this date within our range?
										if (
											dateTime < maximumDate &&
											dateTime >
												minimumDate.minus({
													days: 1,
												})
										) {
											return false
										}

										return true
									}}
								/>
							</div>
						</PopoverPanel>
					</Transition>
				</Popover>
			</PopoverGroup>

			{/** Date End */}
			<PopoverGroup className="flex items-center divide-x divide-gray-200">
				<Popover className="relative z-10 inline-block text-right">
					<PopoverButton
						className={cn(
							"group relative inline-flex items-center justify-center rounded-none",
							"border border-l-0 border-gray-300 bg-white px-3 py-2 text-sm font-medium",
							"text-gray-700 hover:bg-gray-50 hover:text-gray-900",
							"focus:ring-primary-500 focus:z-20 focus:ring-1 focus:outline-hidden",
						)}
					>
						<span className="mr-2">
							{DateTime.fromJSDate(date).toFormat(
								t(
									"project:data.production_today.date_picker.title.date_format",
								),
							)}
						</span>

						<ChevronDownIcon
							className="size-5 shrink-0 text-gray-400 group-hover:text-gray-500"
							aria-hidden="true"
						/>
					</PopoverButton>
					<Transition {...getTransitionPopoverProps()}>
						<PopoverPanel className="absolute left-0 mt-2 origin-top-right lg:right-0 lg:left-auto">
							<div
								className="min-w-[300px]"
								data-testid="daterangepicker-from"
							>
								<DatePicker
									initialMonth={currentDateTime.month}
									initialYear={currentDateTime.year}
									selectedDate={currentDateTime}
									showChangeOfYear={false}
									showNavigation={false}
									months={months}
									onSelectDate={(date) => {
										const newDate =
											DateTime.fromObject(date).endOf(
												"day",
											)
										setDate(newDate.toJSDate())
									}}
									isDateDisabled={(date) => {
										const dateTime = DateTime.fromObject({
											...date,
											day: date.day + 1,
										})

										// is this date within our range?
										if (
											dateTime < maximumDate &&
											dateTime >
												minimumDate.minus({
													days: 1,
												})
										) {
											return false
										}

										return true
									}}
								/>
							</div>
						</PopoverPanel>
					</Transition>
				</Popover>
			</PopoverGroup>
		</div>
	)
}
